// frontend/src/pages/employees/PeriodControl.jsx
import React, { useEffect, useState } from "react";
import { GRANULARITIES } from "./usePeriod";

const LABEL = { day: "Day", month: "Month", year: "Year", custom: "Custom" };

const wrap = {
  display: "flex", alignItems: "center", justifyContent: "space-between",
  gap: 16, flexWrap: "wrap", marginBottom: 24,
};
const segment = {
  display: "inline-flex", border: "1px solid #e5e7eb", borderRadius: 8,
  overflow: "hidden", background: "white",
};
const stepper = {
  display: "inline-flex", alignItems: "center", gap: 12,
};
const arrow = {
  padding: "6px 12px", borderRadius: 6, border: "1px solid #e5e7eb",
  background: "white", cursor: "pointer", fontSize: 16, lineHeight: 1,
};

function segmentButton(active) {
  return {
    padding: "8px 18px", border: "none", cursor: "pointer", fontSize: 14,
    fontWeight: 600,
    background: active ? "#3b82f6" : "white",
    color: active ? "white" : "#6b7280",
  };
}

const customRow = {
  display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
  width: "100%", marginTop: 12,
};
const dateField = {
  display: "flex", alignItems: "center", gap: 6, fontSize: 13,
  color: "#6b7280",
};
const dateInput = {
  padding: "6px 8px", borderRadius: 6, border: "1px solid #e5e7eb",
  fontSize: 14,
};
function applyButton(disabled) {
  return {
    padding: "6px 16px", borderRadius: 6, border: "1px solid #3b82f6",
    background: "#3b82f6", color: "white",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    fontSize: 14, fontWeight: 600,
  };
}
const applyHint = {
  fontSize: 12, color: "#6b7280",
};

/**
 * The page's only chrome: granularity, and a step through it.
 *
 * Presentational on purpose -- the COMMITTED period lives in the URL
 * (EmployeesPage), so this component can be rendered twice on one screen
 * without the two copies disagreeing. In Custom mode the two date inputs
 * hold local, uncommitted state: typing never refetches anything, only
 * Apply (via onCustomRange) commits a new range.
 */
export default function PeriodControl({
  granularity, label, startDate, endDate,
  onGranularity, onStep, onCustomRange,
}) {
  const isCustom = granularity === "custom";
  const unit = isCustom ? "period" : LABEL[granularity].toLowerCase();

  const [from, setFrom] = useState(startDate || "");
  const [to, setTo] = useState(endDate || "");

  // Seed the inputs from the window already on screen whenever Custom
  // becomes active or the committed bounds move (stepping, or another
  // Apply) -- never mid-keystroke, since this effect only fires when the
  // props themselves change.
  useEffect(() => {
    if (isCustom) {
      setFrom(startDate || "");
      setTo(endDate || "");
    }
  }, [isCustom, startDate, endDate]);

  return (
    <div style={wrap}>
      <div style={segment} role="group" aria-label="Period granularity">
        {GRANULARITIES.map((g) => (
          <button
            key={g}
            type="button"
            aria-pressed={granularity === g}
            onClick={() => onGranularity(g)}
            style={segmentButton(granularity === g)}
          >
            {LABEL[g]}
          </button>
        ))}
      </div>

      <div style={stepper}>
        <button
          type="button" style={arrow} onClick={() => onStep(-1)}
          aria-label={`Previous ${unit}`}
        >
          ◀
        </button>
        <span style={{ fontSize: 16, fontWeight: 600, minWidth: 180,
                       textAlign: "center" }}>
          {label}
        </span>
        <button
          type="button" style={arrow} onClick={() => onStep(1)}
          aria-label={`Next ${unit}`}
        >
          ▶
        </button>
      </div>

      {isCustom && (
        <div style={customRow}>
          <label style={dateField}>
            From
            <input
              type="date" style={dateInput} value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </label>
          <label style={dateField}>
            To
            <input
              type="date" style={dateInput} value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </label>
          <button
            type="button" style={applyButton(!from || !to)}
            // A blank date writes a "..2026-03-31"/"2026-01-01.." anchor
            // that fails normalizeAnchor's FROM..TO shape check and
            // silently falls back to the current month -- Apply must not
            // be clickable with one half of the range still empty. The
            // reversed-range case (from > to) is left alone: that has one
            // obvious intent (swap), unlike a missing date. The disabled
            // styling and hint below make that dead state visible instead
            // of leaving a button that looks pressable but silently isn't.
            disabled={!from || !to}
            onClick={() => onCustomRange(from, to)}
          >
            Apply
          </button>
          {(!from || !to) && <span style={applyHint}>Pick both dates</span>}
        </div>
      )}
    </div>
  );
}
